import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = 'baremc_referral_code';

// Cache referral code in memory, seeded synchronously from localStorage
let _cachedRefCode = (() => {
  try { return localStorage.getItem(LS_KEY) || null; } catch { return null; }
})();
let _fetchedRefCode = false;

/**
 * Synchronous accessor — returns the cached referral code (from localStorage on first load,
 * updated to the latest value after getReferralCode() resolves). Returns null if user has no
 * token. Safe to call during render.
 */
export function getReferralCodeSync() {
  try {
    if (!localStorage.getItem('token')) return null;
  } catch { return null; }
  return _cachedRefCode;
}

/**
 * Get the logged-in user's referral code. Returns the cached value immediately if available,
 * and refreshes from the server in the background.
 */
export async function getReferralCode() {
  const token = (() => { try { return localStorage.getItem('token'); } catch { return null; } })();
  if (!token) {
    _cachedRefCode = null;
    try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
    return null;
  }
  if (_cachedRefCode && _fetchedRefCode) return _cachedRefCode;
  try {
    const res = await axios.get(`${API_URL}/points/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const code = res.data?.referral_code || null;
    _cachedRefCode = code;
    _fetchedRefCode = true;
    try {
      if (code) localStorage.setItem(LS_KEY, code);
      else localStorage.removeItem(LS_KEY);
    } catch { /* ignore */ }
    return code;
  } catch {
    _fetchedRefCode = true;
    return _cachedRefCode; // fall back to last-known cached code (if any)
  }
}

/**
 * Build a share URL that includes the user's referral code when logged in.
 * @param {string} pageUrl - The page URL to share (e.g. window.location.href)
 * @returns {Promise<string>} The URL with ?ref= appended if user is logged in
 */
export async function buildShareUrl(pageUrl) {
  const refCode = await getReferralCode();
  if (!refCode) return pageUrl;
  const url = new URL(pageUrl);
  url.searchParams.set('ref', refCode);
  return url.toString();
}

/**
 * Build share URL synchronously using cached code (call getReferralCode first).
 */
export function buildShareUrlSync(pageUrl) {
  if (!_cachedRefCode) return pageUrl;
  const url = new URL(pageUrl);
  url.searchParams.set('ref', _cachedRefCode);
  return url.toString();
}

/**
 * Universal share function.
 * Shares the given URL with the user's referral code appended.
 * Uses Web Share API on mobile, falls back to clipboard copy.
 */
export async function shareWithReferral({ title, text, url, copiedMessage = 'Link copied!' }) {
  const shareUrl = await buildShareUrl(url || window.location.href);
  const refCode = _cachedRefCode;
  const shareText = refCode
    ? `${text || 'Check out BARE MC - The largest MC community!'} Use my link and we both earn points:`
    : (text || 'Check out BARE MC - The largest MC community!');

  if (navigator.share) {
    try {
      await navigator.share({ title: title || 'BareMC', text: shareText, url: shareUrl });
      return true;
    } catch (err) {
      if (err.name === 'AbortError') return false;
    }
  }

  // Fallback: copy to clipboard
  try {
    await navigator.clipboard.writeText(shareUrl);
    toast.success(copiedMessage);
    return true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = shareUrl;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    toast.success(copiedMessage);
    return true;
  }
}

/**
 * Reset cached referral code (call on logout).
 */
export function clearReferralCache() {
  _cachedRefCode = null;
  _fetchedRefCode = false;
  try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
}
